import styled from 'styled-components'
import { TdsButton, TdsIcon, TdsTextField } from '@scania/tegel-react'
import { ActiveChoicesIcon } from './ActiveChoicesIcon'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  getLatestConfigChangeResult,
  getTruckInfoState,
} from '../../store/truckInfoSlice'
import { useActiveChoicesHighlight } from './ActiveChoicesHighlight'
import { openGvPanel, setGvSidePanelContent } from '../../store/sidePanelSlice'
import { GuidedViewPageType, SidePanelContent } from '../../store/types'
import {
  getGuidedViewPage,
  getSessionInitDataState,
} from '../../store/sessionDataSlice'
import { SvgKebab, SvgSearch } from '../../components/SvgImports'
import { getMenuState, openMenuSaveSharePopUp } from '../../store/menuSlice'
import { TextId, useTextsV2 } from '../../utils/useTexts'
import { BreakpointWidthPx } from '../../css/BreakpointWidthPx'
import { BreakpointHeightPx } from '../../css/BreakpointHeightPx'
import {
  pushAdobeEvent,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingSearchClick,
  ScaniaAdobeTrackingSearchResultClick,
  ScaniaAdobeTrackingStartOverClick,
} from '../../utils/adobeAnalytics'
import { useCallback, useState } from 'react'
import { ScaniaSearchResult } from '../../api/generated'
import { getMarketLanguageState } from '../../store/appSlice'
import { useClient } from '../../utils/useClient'
import { SESSION_FAILURE } from '../../api/errors'
import { CustomScrollbarMixin } from '../../css/CustomScrollbarMixin'
import {
  buildRelativeGvPage,
  buildRelativeOverviewPage,
} from '../../utils/UrlBuilders'
import { getUrlParametersToString } from '../../utils/getUrlParametersToString'
import { useNavigate } from 'react-router-dom'
import { TestElementTypeId } from '../../types/TestAttributeId'

const SubHeaderRoot = styled.div`
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: 64px;
  margin-right: 24px;

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    margin-left: 32px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const IconsWrapper = styled.div`
  display: flex;
  height: 40px;
  gap: 8px;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: end;
  gap: 8px;
  font-size: 14px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  svg {
    height: 32px;
  }
`

const IconSearchWrapper = styled.div`
  display: flex;
  align-items: end;
  gap: 8px;
  font-size: 14px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  svg {
    height: 26px;
  }
`

const SearchResultContainer = styled.div`
  ${CustomScrollbarMixin}
  position: absolute;
  width: 230px;
  max-height: calc(100vh - 210px);
  right: 106px;
  top: 128px;
  background-color: var(--tds-grey-50);
  z-index: 1;
  border: 1px solid var(--tds-grey-100);
  overflow-y: scroll;
`

const SearchResult = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  padding-left: 8px;
  border-bottom: 1px solid var(--tds-grey-100);

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      background-color: var(--tds-grey-100);
    }
  }
`

interface SubHeaderProps {
  handleNewClick: () => void
  handleSessionFailure: () => void
  navigateToMenu: (
    id: string,
    menuPath: string,
    gvPageToJumpTo: GuidedViewPageType,
  ) => void
}

export function SubHeader({
  handleNewClick,
  handleSessionFailure,
  navigateToMenu,
}: SubHeaderProps): JSX.Element {
  const dispatch = useAppDispatch()
  const client = useClient()
  const t2 = useTextsV2()
  const navigate = useNavigate()
  const latestConfigChangeResult = useAppSelector(getLatestConfigChangeResult)
  const activeChoicesState = latestConfigChangeResult?.activeChoices
  const highlightYourChoices = useActiveChoicesHighlight(
    activeChoicesState || [],
  )
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const guidedViewPage = useAppSelector(getGuidedViewPage)
  const popUpIsOpen = useAppSelector(getMenuState).menuSaveSharePopUpIsOpen
  const truckInfo = useAppSelector(getTruckInfoState)
  const isFactoryModel = truckInfo?.isFactoryModel
  const [searchExpanded, setSearchExpanded] = useState<boolean>(false)
  const [searchString, setSearchString] = useState<string>('')
  const [searchResult, setSearchResult] = useState<ScaniaSearchResult[] | null>(
    null,
  )
  const { currentlyLoadingId } = useAppSelector(getMenuState)
  let searchLenMin = 2

  const handleActiveChangesClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(openGvPanel())
    dispatch(setGvSidePanelContent(SidePanelContent.activeChanges))
  }

  const handleBackClick = () => {
    if (currentlyLoadingId) {
      console.log(
        'Ignored a button click while waiting for a pending config change.',
      )
      return
    }
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    if (guidedViewPage === GuidedViewPageType.overview) {
      handleNewClick()
      const trackingEvent: ScaniaAdobeTrackingStartOverClick = {
        event: ScaniaAdobeEventId.StartOverClick,
        eventInfo: { layout: 'desktop' },
      }
      pushAdobeEvent(trackingEvent)
    } else if (guidedViewPage === GuidedViewPageType.guidedView) {
      const relativeUrl = buildRelativeOverviewPage(marketLanguage)
      const params = relativeUrl.params
      const newUrl = relativeUrl.path + getUrlParametersToString(params)
      navigate(newUrl)
    } else if (guidedViewPage === GuidedViewPageType.allView) {
      if (isFactoryModel) {
        const relativeUrl = buildRelativeOverviewPage(marketLanguage)
        const params = relativeUrl.params
        const newUrl = relativeUrl.path + getUrlParametersToString(params)
        navigate(newUrl)
      } else {
        const relativeUrl = buildRelativeGvPage(marketLanguage)
        const params = relativeUrl.params
        const newUrl = relativeUrl.path + getUrlParametersToString(params)
        navigate(newUrl)
      }
    }
  }

  const handleKebabMenuClick = (e: React.MouseEvent) => {
    if (!popUpIsOpen) {
      e.stopPropagation()
      dispatch(openMenuSaveSharePopUp())
    }
  }

  const handleSearchIconClick = () => {
    setSearchExpanded(true)
  }

  const handleSearch = useCallback(
    async (event: any) => {
      if (!sessionInitData) {
        throw new Error('Expected sessionInitData to be defined.')
      }
      if (!marketLanguage) {
        throw new Error('Expected marketLanguage to be defined.')
      }
      if (!client) {
        throw new Error('Expected client to be defined.')
      }
      const typedSearchString = event.target.value
      setSearchString(typedSearchString)
      if (typedSearchString && typedSearchString.length >= searchLenMin) {
        let searchResultTyped
        try {
          const response = await client.scaniaSearch(
            sessionInitData.sessionId,
            typedSearchString,
          )
          if (response.error === SESSION_FAILURE) {
            handleSessionFailure()
            return
          }
          // TODO: Investigate if null or undefined in the response is an error
          // or not.
          searchResultTyped = response.scaniaSearch || null
        } catch {
          console.log('Search went wrong.')
          return
        }
        setSearchResult(searchResultTyped || null)
      } else {
        setSearchResult(null)
      }
    },
    [
      client,
      marketLanguage,
      sessionInitData,
      searchLenMin,
      handleSessionFailure,
    ],
  )

  const handleSearchClick = () => {
    const trackingEvent: ScaniaAdobeTrackingSearchClick = {
      event: ScaniaAdobeEventId.SearchClick,
    }
    pushAdobeEvent(trackingEvent)
  }

  const handleSearchResultClick = (
    clickedResult: ScaniaSearchResult,
    resultCount: number,
  ) => {
    const trackingEvent: ScaniaAdobeTrackingSearchResultClick = {
      event: ScaniaAdobeEventId.SearchResultClick,
      eventInfo: {
        searchTerm: searchString,
        searchResultCount: resultCount,
        searchResultClickTitle: clickedResult.description,
      },
    }
    pushAdobeEvent(trackingEvent)
    navigateToMenu(
      clickedResult.id,
      clickedResult.path,
      GuidedViewPageType.allView,
    )
    setSearchString('')
    setSearchResult(null)
  }

  const capitalizeFirstLetter = (text: string | null | undefined) => {
    if (!text) {
      return ''
    }
    const smallText = text.toLowerCase()
    const capitalizedText =
      smallText.charAt(0).toUpperCase() + smallText.slice(1)
    return capitalizedText || ''
  }

  const textSearchPlaceholde = capitalizeFirstLetter(t2(TextId.SEARCH))

  return (
    <SubHeaderRoot>
      <TdsButton
        variant="ghost"
        text={
          guidedViewPage === GuidedViewPageType.overview
            ? t2(TextId.ACTION_LABEL_START_OVER)
            : guidedViewPage === GuidedViewPageType.guidedView
            ? t2(TextId.ACTION_LABEL_OVERVIEW)
            : guidedViewPage === GuidedViewPageType.allView && isFactoryModel
            ? t2(TextId.ACTION_LABEL_OVERVIEW)
            : t2(TextId.ACTION_LABEL_VIEW_LESS)
        }
        onClick={handleBackClick}
        data-test-element-type={TestElementTypeId.SubHeaderGoBackButton}
      >
        <TdsIcon slot="icon" size="18px" name="back" />
      </TdsButton>
      <IconsWrapper>
        {guidedViewPage === GuidedViewPageType.allView && !searchExpanded && (
          <IconSearchWrapper
            onClick={handleSearchIconClick}
            data-test-element-type={TestElementTypeId.SubHeaderSearch}
          >
            <SvgSearch />
          </IconSearchWrapper>
        )}
        {guidedViewPage === GuidedViewPageType.allView && searchExpanded && (
          <TdsTextField
            type="text"
            onInput={handleSearch}
            value={searchString}
            placeholder={textSearchPlaceholde}
            size="md"
            onClick={handleSearchClick}
          >
            <TdsIcon slot="suffix" name="search" size="20px" />
          </TdsTextField>
        )}
        {guidedViewPage !== GuidedViewPageType.overview && (
          <IconWrapper onClick={handleActiveChangesClick}>
            <ActiveChoicesIcon
              choiceCount={activeChoicesState?.length || 0}
              isHighlighted={highlightYourChoices}
              data-test-element-type={
                TestElementTypeId.SubHeaderActiveChoicesButton
              }
            />
          </IconWrapper>
        )}
        {guidedViewPage !== GuidedViewPageType.overview && (
          <IconWrapper
            onClick={handleKebabMenuClick}
            data-test-element-type={TestElementTypeId.SubHeaderKebabButton}
          >
            <SvgKebab />
          </IconWrapper>
        )}
      </IconsWrapper>
      {searchResult && searchResult.length !== 0 && (
        <SearchResultContainer>
          {searchResult.map((search) => (
            <SearchResult
              key={search.id}
              onClick={() =>
                handleSearchResultClick(search, searchResult.length)
              }
            >
              {search.description}
            </SearchResult>
          ))}
        </SearchResultContainer>
      )}
      {searchResult && searchResult.length === 0 && (
        <SearchResultContainer>
          <SearchResult>{t2(TextId.SEARCH_NO_HITS)}</SearchResult>
        </SearchResultContainer>
      )}
      {searchString && searchString.length < searchLenMin && (
        <SearchResultContainer>
          <SearchResult>{t2(TextId.SEARCH_MIN_LENGTH)}</SearchResult>
        </SearchResultContainer>
      )}
    </SubHeaderRoot>
  )
}
